import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { ConversationSummary } from '@/services/api/chatApi';

interface ChatState {
  conversations: ConversationSummary[];
  activeConversationId: string | null;
  isLoadingConversation: boolean;
  /** LLM tokens the active conversation has used, for the chat length ring. */
  conversationTokens: number;
  /** Whose history is on screen. Lags `activeConversationId`, which a sidebar
   *  pick sets before the history load starts. */
  loadedConversationId: string | null;

  setActiveConversation: (id: string | null) => void;
  setConversations: (conversations: ConversationSummary[]) => void;
  setLoadingConversation: (loading: boolean) => void;
  setConversationTokens: (tokens: number) => void;
  setLoadedConversation: (id: string | null) => void;
  clearChat: () => void;
}

export const useChatStore = create<ChatState>()(
  devtools(
    set => ({
      conversations: [],
      activeConversationId: null,
      isLoadingConversation: false,
      conversationTokens: 0,
      loadedConversationId: null,

      setActiveConversation: id =>
        set({ activeConversationId: id }, false, 'chat/setActiveConversation'),

      setConversations: conversations => set({ conversations }, false, 'chat/setConversations'),

      setLoadingConversation: loading =>
        set({ isLoadingConversation: loading }, false, 'chat/setLoadingConversation'),

      setConversationTokens: tokens =>
        set({ conversationTokens: tokens }, false, 'chat/setConversationTokens'),

      setLoadedConversation: id =>
        set({ loadedConversationId: id }, false, 'chat/setLoadedConversation'),

      clearChat: () =>
        set(
          {
            activeConversationId: null,
            isLoadingConversation: false,
            conversationTokens: 0,
            loadedConversationId: null,
          },
          false,
          'chat/clearChat'
        ),
    }),
    { name: 'ChatStore', enabled: import.meta.env.DEV }
  )
);

/**
 * The active conversation's id, waiting up to `timeoutMs` for a brand-new chat
 * to get one: `useChat` only learns it after the first reply finishes.
 */
export function waitForActiveConversation(timeoutMs = 5000): Promise<string | null> {
  const current = useChatStore.getState().activeConversationId;
  if (current) return Promise.resolve(current);

  return new Promise(resolve => {
    const timer = setTimeout(() => {
      unsubscribe();
      resolve(null);
    }, timeoutMs);
    const unsubscribe = useChatStore.subscribe(state => {
      if (!state.activeConversationId) return;
      clearTimeout(timer);
      unsubscribe();
      resolve(state.activeConversationId);
    });
  });
}
